/**
 * Lenavio Pulse API client.
 *
 * Mirror of ``backend/app/routers/pulse.py``. Keeps types here so the
 * shape is the single source of truth for every consumer (list page,
 * builder, respond form, results dashboard, sidebar gate).
 */
import client from "./client";

// ── Question / survey types ────────────────────────────────────────

export type PulseQuestionType =
  | "scale_1_5"
  | "scale_1_10"
  | "text"
  | "multiple_choice";

export type PulseFrequency = "weekly" | "monthly" | "quarterly" | "one_time";

export interface PulseQuestion {
  id: string;
  survey_id: string;
  question_text: string;
  question_type: PulseQuestionType;
  options: string[] | null;
  order_index: number;
}

export interface PulseQuestionInput {
  question_text: string;
  question_type: PulseQuestionType;
  options?: string[] | null;
  order_index?: number;
}

export interface PulseSurvey {
  id: string;
  org_id: string;
  title: string;
  description: string | null;
  frequency: PulseFrequency;
  is_anonymous: boolean;
  is_active: boolean;
  is_default_template: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  questions: PulseQuestion[];
  response_count: number;
  has_responded: boolean;
}

export interface PulseSurveyCreatePayload {
  title: string;
  description?: string;
  frequency: PulseFrequency;
  is_anonymous: boolean;
  questions?: PulseQuestionInput[];
}

export interface PulseSurveyUpdatePayload {
  title?: string;
  description?: string;
  frequency?: PulseFrequency;
  is_anonymous?: boolean;
  is_active?: boolean;
}

// ── Response submission ────────────────────────────────────────────

export interface PulseAnswerInput {
  question_id: string;
  rating?: number | null;
  text_answer?: string | null;
}

// ── Aggregate / dashboard types ────────────────────────────────────

export interface QuestionAggregate {
  question_id: string;
  question_text: string;
  question_type: PulseQuestionType;
  response_count: number;
  average: number | null;
  distribution: Record<string, number>;
  text_answers: string[];
}

export interface ENPSBreakdown {
  promoters: number;
  passives: number;
  detractors: number;
  total: number;
  score: number; // -100..+100
}

export interface SurveyResults {
  survey_id: string;
  title: string;
  is_anonymous: boolean;
  response_rate: number;
  total_respondents: number;
  eligible_employees: number;
  questions: QuestionAggregate[];
  enps: ENPSBreakdown | null;
  engagement_score: number | null;
}

export interface PulseTrendPoint {
  period_label: string;
  engagement_score: number | null;
  response_count: number;
}

export interface DepartmentBreakdown {
  department_id: string | null;
  department_name: string;
  response_count: number;
  engagement_score: number | null;
}

export interface PulseDashboard {
  engagement_score: number | null;
  enps: ENPSBreakdown | null;
  response_rate: number;
  total_responses_30d: number;
  trend: PulseTrendPoint[];
  department_breakdown: DepartmentBreakdown[];
}

// ── Endpoints ──────────────────────────────────────────────────────

export async function listSurveys(includeInactive = false): Promise<PulseSurvey[]> {
  const res = await client.get<PulseSurvey[]>("/pulse/surveys", {
    params: { include_inactive: includeInactive },
  });
  return res.data;
}

export async function getSurvey(id: string): Promise<PulseSurvey> {
  const res = await client.get<PulseSurvey>(`/pulse/surveys/${id}`);
  return res.data;
}

export async function createSurvey(
  data: PulseSurveyCreatePayload,
): Promise<PulseSurvey> {
  const res = await client.post<PulseSurvey>("/pulse/surveys", data);
  return res.data;
}

export async function updateSurvey(
  id: string,
  data: PulseSurveyUpdatePayload,
): Promise<PulseSurvey> {
  const res = await client.patch<PulseSurvey>(`/pulse/surveys/${id}`, data);
  return res.data;
}

export async function addQuestions(
  surveyId: string,
  questions: PulseQuestionInput[],
): Promise<PulseSurvey> {
  const res = await client.post<PulseSurvey>(
    `/pulse/surveys/${surveyId}/questions`,
    questions,
  );
  return res.data;
}

export async function submitResponse(
  surveyId: string,
  answers: PulseAnswerInput[],
): Promise<{ inserted: number; status: string }> {
  const res = await client.post(`/pulse/surveys/${surveyId}/respond`, {
    answers,
  });
  return res.data;
}

export async function getResults(surveyId: string): Promise<SurveyResults> {
  const res = await client.get<SurveyResults>(
    `/pulse/surveys/${surveyId}/results`,
  );
  return res.data;
}

export async function getDashboard(): Promise<PulseDashboard> {
  const res = await client.get<PulseDashboard>("/pulse/dashboard");
  return res.data;
}

// ════════════════════════════════════════════════════════════════════
// Send (Manager+ only) — broadcast a survey to all employees or a
// specific list of recipients. Gated by SEND_PULSE on the backend;
// employees calling this directly get a 403.
// ════════════════════════════════════════════════════════════════════

export interface PulseSendPayload {
  /** ``"all"`` notifies every active employee in the org;
   *  ``"employees"`` only notifies the listed ids. */
  target: "all" | "employees";
  employee_ids?: string[];
  /** Optional custom note appended to the notification body. */
  message?: string;
}

export interface PulseSendResult {
  sent_count: number;
  skipped_count: number;
  sent_to: { employee_id: string; name: string }[];
}

export async function sendSurvey(
  surveyId: string,
  payload: PulseSendPayload,
): Promise<PulseSendResult> {
  const res = await client.post<PulseSendResult>(
    `/pulse/surveys/${surveyId}/send`,
    payload,
  );
  return res.data;
}
